/**
 * AUTOMATION MONITORING — Monitoraggio esecuzioni di una singola automazione
 * (schermata id="monitor" del mockup). Legge l'id dalla rotta
 * `/automations/:id/monitoring` e cabla i dati reali: header via getAutomation,
 * KPI via /stats (con tempo medio), log run via /runs. Finché il motore non
 * scrive AutomationRun, i KPI sono 0 e il log è vuoto.
 */

import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { formatDateTime, formatNanos, formatRelativeDate } from '@crm/shared';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { exclusionReasons } from './automationExclusionReasons';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { LoadingOverlay } from '../../components/LoadingOverlay';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { useToast } from '../../components/useToast';
import {
  deleteAutomationRun,
  deleteEmptyAutomationRuns,
  fetchAutomation,
  fetchAutomationConditionTypes,
  fetchAutomationRunExecutions,
  fetchAutomationRuns,
  fetchAutomationStats,
} from './automationThunks';
import type {
  AutomationExecutionStatus,
  AutomationRun,
  AutomationRunStatus,
  AutomationStatus,
} from './automationTypes';

// Stato run -> classe del pallino (status-dot) e chiave i18n dell'etichetta.
const RUN_STATUS_DOT: Record<AutomationRunStatus, 'ok' | 'fail' | 'skip'> = {
  success: 'ok',
  failed: 'fail',
  running: 'skip',
};
const RUN_STATUS_LABEL_KEY: Record<AutomationRunStatus, string> = {
  success: 'statusOk',
  failed: 'statusFailed',
  running: 'statusRunning',
};

// Esito sul singolo record -> pallino ed etichetta. Solo `success` e `failed` sono un verdetto sul
// LAVORO: gli altri quattro dicono che il lavoro non è stato fatto, e per ragioni diverse — non
// idoneo, già trattato, fermato dall'anti-loop, ancora in corso. Il pallino li accomuna, l'etichetta
// li distingue, ed è la distinzione che il log dei run da solo non poteva mostrare.
const EXEC_STATUS_DOT: Record<AutomationExecutionStatus, 'ok' | 'fail' | 'skip'> = {
  success: 'ok',
  failed: 'fail',
  running: 'skip',
  skipped: 'skip',
  'already-executed': 'skip',
  'loop-prevented': 'skip',
};
const EXEC_STATUS_LABEL_KEY: Record<AutomationExecutionStatus, string> = {
  success: 'execStatusSuccess',
  failed: 'execStatusFailed',
  running: 'execStatusRunning',
  skipped: 'execStatusSkipped',
  'already-executed': 'execStatusAlreadyExecuted',
  'loop-prevented': 'execStatusLoopPrevented',
};

// Quanto della prima riga di uno stacktrace entra in cella. Il taglio sta nel TESTO e non nel CSS
// perche' la colonna deve restare larga quanto la tabella decide: un max-width in caratteri la
// farebbe spingere dal contenuto, che qui e' lungo a piacere. Il messaggio integrale resta nel
// dettaglio, a un clic.
const ERROR_SUMMARY_MAX = 200;

/**
 * Il testo, tagliato a `max` caratteri con un'ellissi quando serve davvero: senza, un messaggio
 * tagliato si legge come un messaggio finito, e chi guarda non saprebbe di doverlo aprire.
 */
function truncate(text: string | undefined, max: number): string | null {
  if (!text) return null;

  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// Stato automazione -> modificatore del badge dell'header.
const STATUS_BADGE_CLASS: Record<AutomationStatus, string> = {
  active: 'builder-badge--success',
  suspended: 'builder-badge--warning',
  draft: '',
  archived: '',
};

// Selettore periodo: valore = giorni di lookback ancorati all'inizio di oggi (mezzanotte locale),
// tradotti in startDateFrom sul filtro backend di /stats e /runs. 0 = "oggi", -1 = "tutto" (nessun
// filtro). Etichette in i18n periods.<valore>.
const PERIOD_DAYS = [-1, 0, 3, 7, 30, 90, 180, 365] as const;

// Traduce la selezione del combo nell'intervallo backend: startDateFrom = mezzanotte di (oggi -
// giorni), startDateTo aperto (i run non sono nel futuro). "tutto" (-1) = nessun estremo -> nessun
// filtro. Ancorare a mezzanotte evita che "oggi" (0) parta da 24h fa.
function periodRange(days: number): { startDateFrom?: string; startDateTo?: string } {
  if (days < 0) return {};
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  from.setDate(from.getDate() - days);
  return { startDateFrom: from.toISOString() };
}

export default function AutomationMonitoring() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const automationDefinitionId = Number(id);
  const locale = i18n.language.replace(/_/g, '-');

  const confirm = useConfirmDialog();
  const toast = useToast();
  // Le azioni di riga del log sono riservate all'amministratore CRM, come nell'elenco: stesso
  // flag, stessa natura — l'enforce reale resta il permission check del server, che su questa
  // cancellazione pretende il DELETE sull'automazione.
  const isAdmin = useAppSelector((s) => s.auth.user?.administrator) ?? false;
  // Quante colonne ha davvero la tabella dei run: quattro, più quella delle azioni quando c'è.
  // Serve al colSpan della riga vuota e a quello del drill-down, che devono attraversarle tutte —
  // un numero scritto a mano si scorderebbe alla prima colonna aggiunta, e il dettaglio finirebbe
  // per occuparne una in meno lasciando una cella spuria in fondo alla riga.
  const runColumnCount = isAdmin ? 5 : 4;
  const automation = useAppSelector((s) => s.automations.byId[automationDefinitionId]);
  const stats = useAppSelector((s) => s.automations.statsById[automationDefinitionId]);
  const runs = useAppSelector((s) => s.automations.runsById[automationDefinitionId]) ?? [];

  // Periodo selezionato in giorni; default 7 (ultimi 7 giorni al caricamento). "tutto" (0) resta
  // un'opzione del combo.
  const [periodDays, setPeriodDays] = useState<number>(7);
  const [periodAnchor, setPeriodAnchor] = useState<null | HTMLElement>(null);

  // Run aperto nel drill-down: uno alla volta. Riaprire lo stesso lo chiude, come ci si aspetta da
  // una riga espandibile.
  const [openRunId, setOpenRunId] = useState<number | null>(null);
  // Esecuzione di cui e' aperto lo stacktrace. Uno alla volta, come i run: due muri di testo
  // aperti insieme non si confrontano, si sommano.
  const [openExecutionId, setOpenExecutionId] = useState<number | null>(null);
  const [cleaningEmptyRuns, setCleaningEmptyRuns] = useState(false);
  // Fallimento del fetch degli esiti, per run. Serve a distinguere "non ho ancora risposto" da
  // "ho chiesto e non ce l'ho fatta": senza, la riga resterebbe in caricamento per sempre, e
  // riaprirla non ritenterebbe nemmeno — la cache vuota sembra un fetch mai partito.
  const [executionsErrorByRunId, setExecutionsErrorByRunId] = useState<Record<number, boolean>>({});
  const executionsByRunId = useAppSelector((s) => s.automations.executionsByRunId);
  // Catalogo dei campi condizione dell'entità automatizzata: serve a comporre il PERCHÉ di un
  // record scartato, non a mostrare il monitoraggio. Cache per className nello slice, quindi
  // arrivarci due volte non costa una seconda chiamata.
  const conditionFields = useAppSelector((s) =>
    automation ? (s.automations.conditionTypesByClassName[automation.className] ?? []) : [],
  );

  // Campi condizione dell'entità: si chiedono quando l'automazione è arrivata, perché è lei a dire
  // di quale entità si tratta. Senza, `exclusionReasons` non compone nulla — per contratto rende
  // zero motivi invece di una lista di segnaposto.
  useEffect(() => {
    if (automation?.className) dispatch(fetchAutomationConditionTypes(automation.className));
  }, [automation?.className, dispatch]);

  // Header dell'automazione: indipendente dal periodo.
  useEffect(() => {
    if (!automationDefinitionId) return;
    dispatch(fetchAutomation(automationDefinitionId));
  }, [automationDefinitionId, dispatch]);

  // KPI + log: rifetch al cambio periodo, tradotto in startDateFrom/startDateTo.
  useEffect(() => {
    if (!automationDefinitionId) return;
    const range = periodRange(periodDays);

    // Il drill-down si chiude: la riga aperta appartiene alla lista che sta per essere sostituita,
    // e restare "aperti" su un run che non è più a video non significa niente.
    setOpenRunId(null);

    dispatch(fetchAutomationStats({ automationDefinitionId, ...range }));
    dispatch(fetchAutomationRuns({ automationDefinitionId, ...range }));
  }, [automationDefinitionId, periodDays, dispatch]);

  const runCount = stats?.runCount ?? 0;
  const successCount = stats?.successCount ?? 0;
  const failureCount = stats?.failureCount ?? 0;
  const fmtNum = (n: number, frac = 0) =>
    n.toLocaleString(locale, { minimumFractionDigits: frac, maximumFractionDigits: frac });
  const pct = (n: number) => (runCount ? (n / runCount) * 100 : 0);
  // Durata del run: campo persistito in NANOSECONDI, come ogni durata delle automazioni — dalla
  // singola condizione valutata al run intero. `formatNanos` sceglie l'unità in base all'ordine di
  // grandezza, quindi un run veloce si legge in millisecondi e uno lungo in secondi o minuti.
  // Per i run ancora in corso il campo è 0 -> mostriamo "—", che è diverso da "0 s".
  const durationLabel = (run: AutomationRun) =>
    run.endDate ? formatNanos(run.duration, locale) : '—';

  // Apre o chiude la riga, e fetcha solo la prima volta: gli esiti di un run concluso non cambiano
  // più, quindi riaprirlo non ha nulla da rileggere.
  /**
   * Cancella una passata e il suo sottoalbero. La riga esce dal log senza rifetch — la lista a
   * video è già quella giusta meno una — e con essa il drill-down, che lo slice toglie dalla
   * cache perché il server ha cancellato gli esiti in cascata.
   * <p>Se la riga cancellata era APERTA, la si chiude: `openRunId` punterebbe a un run che non
   * esiste più, e la riga di dettaglio resterebbe a video sotto una riga sparita.
   * <p>Il fallimento più probabile è il 400 su una passata ancora viva, e il messaggio arriva
   * tradotto dal server: mostrarlo com'è dice "attendi che finisca", che è l'unica cosa utile.
   */
  const performDeleteRun = async (automationRunId: number) => {
    try {
      await dispatch(deleteAutomationRun({ automationDefinitionId, automationRunId })).unwrap();

      if (openRunId === automationRunId) {
        setOpenRunId(null);
        setOpenExecutionId(null);
      }

      // I KPI in alto vanno RILETTI dal server, non corretti qui: sono aggregati — quante passate,
      // quante riuscite, il tempo MEDIO, quante azioni eseguite — e una media non si aggiorna
      // togliendo un addendo. Lo slice sa rimuovere il run dalla lista perché quella è una
      // collezione di righe; i box in alto sono un calcolo, e l'unico che sa rifarlo è chi lo ha
      // fatto la prima volta.
      // Con il periodo CORRENTE, lo stesso dei due fetch iniziali: i KPI sono filtrati per
      // intervallo, e rileggerli senza filtro mostrerebbe numeri di un altro periodo.
      dispatch(
        fetchAutomationStats({ automationDefinitionId, ...periodRange(periodDays) }),
      );

      toast({ severity: 'success', message: t('automation.monitoring.toastRunDeleted') });
    } catch (err) {
      toast({ severity: 'error', message: String(err) });
    }
  };

  /**
   * Toglie dallo storico le passate che non hanno trattato nessun record.
   * <p><b>Rifetch di lista e KPI</b>, e non una correzione a video: quali righe siano sparite qui
   * non si sa — il criterio lo applica il server — e i box in alto sono aggregati, che non si
   * aggiornano togliendo addendi. Con il periodo CORRENTE, come ogni altra rilettura di questa
   * pagina, o si mostrerebbero numeri di un altro intervallo.
   * <p>Il drill-down si chiude prima: la riga aperta può essere una di quelle appena cancellate, e
   * il rifetch la sostituirebbe lasciando a video un dettaglio senza più la sua riga.
   */
  const performCleanupEmptyRuns = async () => {
    setCleaningEmptyRuns(true);

    try {
      await dispatch(deleteEmptyAutomationRuns({ automationDefinitionId })).unwrap();

      setOpenRunId(null);
      setOpenExecutionId(null);

      const range = periodRange(periodDays);

      // Il velo resta finché anche il log è tornato: togliendolo alla risposta del DELETE si
      // vedrebbe per un istante la tabella di prima, con dentro le righe appena cancellate.
      await Promise.all([
        dispatch(fetchAutomationRuns({ automationDefinitionId, ...range })),
        dispatch(fetchAutomationStats({ automationDefinitionId, ...range })),
      ]);

      toast({ severity: 'success', message: t('automation.monitoring.toastEmptyRunsCleaned') });
    } catch (err) {
      toast({ severity: 'error', message: String(err) });
    } finally {
      setCleaningEmptyRuns(false);
    }
  };

  const toggleRun = async (automationRunId: number) => {
    const next = openRunId === automationRunId ? null : automationRunId;

    setOpenRunId(next);
    // Il dettaglio appartiene a una riga del run che sta per sparire dalla vista.
    setOpenExecutionId(null);

    // Già in cache: gli esiti di un run concluso non cambiano più, non c'è nulla da rileggere.
    if (next === null || executionsByRunId[next]) return;

    setExecutionsErrorByRunId((prev) => ({ ...prev, [next]: false }));

    try {
      await dispatch(
        fetchAutomationRunExecutions({ automationDefinitionId, automationRunId: next }),
      ).unwrap();
    } catch {
      // La causa non si mostra: al lettore non serve sapere se è stato un 404 o la rete, gli
      // serve poter riprovare. Riaprire la riga rifà la richiesta, perché la cache è rimasta
      // vuota.
      setExecutionsErrorByRunId((prev) => ({ ...prev, [next]: true }));
    }
  };

  return (
    <div className="builder-layout">
      <div className="builder-main">
        {/* Si torna all'AUTOMAZIONE e non all'elenco: al monitoraggio ci si arriva dalla vista del
            record, e riportare all'elenco farebbe perdere il posto a chi sta guardando quel
            record. Stessa scelta della simulazione, che ha lo stesso rapporto col builder. */}
        <Link
          to={`/automations/${automationDefinitionId}`}
          className="text-sm text-primary builder-back"
        >
          &larr; {t('automation.monitoring.backToAutomation')}
        </Link>

        {/* Barra: nome automazione + badge stato/versione */}
        <div className="builder-topbar">
          <div className="builder-topbar__title">
            <b>{automation?.name ?? ''}</b>
            {automation && (
              <span className={`builder-badge ${STATUS_BADGE_CLASS[automation.status]}`}>
                {t(`automationsInsights.list.statuses.${automation.status}`)} · v{automation.version}
              </span>
            )}
          </div>
          {/* Periodo (selettore display-only). Lo stato dell'automazione qui si legge soltanto:
              sospendere e riattivare si fanno dall'elenco, dove si vedono tutte insieme. */}
          <div className="builder-topbar__modes">
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => setPeriodAnchor(e.currentTarget)}
            >
              {t(`automation.monitoring.periods.${periodDays}`)} ▾
            </Button>
            <Menu
              anchorEl={periodAnchor}
              open={Boolean(periodAnchor)}
              onClose={() => setPeriodAnchor(null)}
            >
              {PERIOD_DAYS.map((days) => (
                <MenuItem
                  key={days}
                  selected={days === periodDays}
                  onClick={() => {
                    setPeriodDays(days);
                    setPeriodAnchor(null);
                  }}
                >
                  {t(`automation.monitoring.periods.${days}`)}
                </MenuItem>
              ))}
            </Menu>
          </div>
        </div>

        {/* KPI */}
        <div className="mon-stats">
          <div className="builder-card">
            <div className="mon-kpi">
              <div className="mon-kpi__n">{fmtNum(runCount)}</div>
              <div className="mon-kpi__l">{t('automation.monitoring.execTotal')}</div>
              <div className="mon-kpi__t text-muted">
                {t('automation.monitoring.lastRun')}{' '}
                {stats?.lastRunDate ? formatRelativeDate(stats.lastRunDate, locale) : '—'}
              </div>
            </div>
          </div>
          <div className="builder-card">
            <div className="mon-kpi">
              <div className="mon-kpi__n mon-kpi__n--ok">{fmtNum(successCount)}</div>
              <div className="mon-kpi__l">{t('automation.monitoring.execOk')}</div>
              <div className="mon-kpi__t mon-kpi__t--up">{fmtNum(pct(successCount), 1)}%</div>
            </div>
          </div>
          <div className="builder-card">
            <div className="mon-kpi">
              <div className="mon-kpi__n mon-kpi__n--fail">{fmtNum(failureCount)}</div>
              <div className="mon-kpi__l">{t('automation.monitoring.execFailed')}</div>
              <div className="mon-kpi__t mon-kpi__t--down">{fmtNum(pct(failureCount), 1)}%</div>
            </div>
          </div>
          <div className="builder-card">
            <div className="mon-kpi">
              {/* Media delle durate dei run completati, in nanosecondi come la colonna di cui è la
                  media. Stesso formattatore della simulazione: le due schermate mostrano tempi
                  della stessa famiglia — decidere e fare — e devono renderli allo stesso modo. */}
              <div className="mon-kpi__n">{formatNanos(stats?.averageDuration ?? 0, locale)}</div>
              <div className="mon-kpi__l">{t('automation.monitoring.avgTime')}</div>
              <div className="mon-kpi__t text-muted">
                {t('automation.monitoring.avgTimeActions', {
                  n: fmtNum(stats?.actionExecutionCount ?? 0),
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Log esecuzioni */}
        <section className="builder-card">
          <div className="builder-card__head">
            {t('automation.monitoring.recentLogs')}
            {/* Stessa mano dell'azione di riga, e stesso gate: chi non può cancellare una passata
                non può nemmeno ripulirle tutte. Qui però il bersaglio non è una riga a video ma un
                criterio, e la conferma è l'unico posto in cui dirlo. */}
            {isAdmin && (
              <Button
                className="mon-log__cleanup"
                size="small"
                variant="outlined"
                color="error"
                disabled={cleaningEmptyRuns}
                onClick={() =>
                  confirm.requestConfirm(
                    t('automation.monitoring.confirmCleanupEmptyRuns'),
                    performCleanupEmptyRuns,
                  )
                }
              >
                {t('automation.monitoring.cleanupEmptyRuns')}
              </Button>
            )}
          </div>
          <table className="simtable">
            <thead>
              <tr>
                <th>{t('automation.monitoring.colDateTime')}</th>
                <th>{t('automation.monitoring.colStatus')}</th>
                <th>{t('automation.monitoring.colRecords')}</th>
                <th>{t('automation.monitoring.colDuration')}</th>
                {/* La colonna azioni esiste solo per chi ha azioni da fare: per tutti gli altri
                    la tabella resta a quattro colonne invece di portarne una vuota. È il motivo
                    per cui il colSpan qui sotto si conta e non si scrive. */}
                {isAdmin && <th>{t('automation.monitoring.colActions')}</th>}
              </tr>
            </thead>
            <tbody>
              {runs.length === 0 ? (
                <tr>
                  <td colSpan={runColumnCount} className="text-muted text-center p-lg">
                    {t('automation.monitoring.noRuns')}
                  </td>
                </tr>
              ) : (
                runs.map((run) => {
                  const open = openRunId === run.automationRunId;
                  const executions = executionsByRunId[run.automationRunId];
                  const failed = executionsErrorByRunId[run.automationRunId];
                  // Le colonne sono l'UNIONE delle etichette viste, non quelle della prima riga: un
                  // record cancellato o non più visibile degrada alla sola chiave primaria, quindi
                  // porta una colonna sola mentre gli altri ne portano due. Prendendole dalla prima
                  // riga, la tabella si disallineerebbe sulla prima eccezione.
                  const recordColumns = Array.from(
                    new Set((executions ?? []).flatMap((e) => e.displayValues.map((v) => v.label))),
                  );

                  return (
                    <Fragment key={run.automationRunId}>
                      <tr
                        className="simtable__row--clickable"
                        onClick={() => toggleRun(run.automationRunId)}
                      >
                        <td>
                          {/* Con i secondi: un solo salvataggio puo' produrre piu' passate a
                              distanza di millisecondi, e senza di essi due righe consecutive
                              sembrano la stessa ripetuta. */}
                          {open ? '▾' : '▸'} {formatDateTime(run.startDate, locale, true)}
                        </td>
                        <td>
                          <span className={`status-dot status-dot--${RUN_STATUS_DOT[run.status]}`}>
                            <span className="status-dot__d" />
                            {t(`automation.monitoring.${RUN_STATUS_LABEL_KEY[run.status]}`)}
                          </span>
                        </td>
                        <td>{run.processedItems}</td>
                        <td>{durationLabel(run)}</td>
                        {/* La cella FERMA il click: la riga intera apre e chiude il drill-down, e
                            senza stopPropagation aprire il menu aprirebbe anche il dettaglio —
                            che poi resterebbe aperto sotto una riga in procinto di sparire. */}
                        {isAdmin && (
                          <td onClick={(e) => e.stopPropagation()}>
                            <RunRowActions
                              automationRunId={run.automationRunId}
                              onDelete={(automationRunId) =>
                                confirm.requestConfirm(
                                  t('automation.monitoring.confirmDeleteRun'),
                                  () => performDeleteRun(automationRunId),
                                )
                              }
                            />
                          </td>
                        )}
                      </tr>

                      {open && (
                        <tr>
                          <td colSpan={runColumnCount}>
                            {/* L'errore del RUN, quando c'è: riguarda la passata intera e non un
                                record — tipicamente la risoluzione dell'entità, che fallisce allo
                                stesso modo su tutti. Sta sopra gli esiti perché se il run è
                                morto lì sotto potrebbe non esserci niente, e in <pre> perché il
                                campo contiene lo stacktrace: a capo dove li ha il testo, e scroll
                                invece di una cella alta mille righe. */}
                            {run.errorMessage && (
                              <div className="mon-error-detail">
                                <div className="mon-error-detail__title">
                                  {t('automation.monitoring.errorDetail')}
                                </div>
                                <pre className="mon-error-detail__body">{run.errorMessage}</pre>
                              </div>
                            )}

                            {failed ? (
                              <div className="text-muted p-md">
                                {t('automation.monitoring.executionsFailed')}
                              </div>
                            ) : !executions ? (
                              <div className="text-muted p-md">
                                {t('automation.monitoring.loadingExecutions')}
                              </div>
                            ) : executions.length === 0 ? (
                              <div className="text-muted p-md">
                                {/* Due messaggi diversi, perché sono due fatti diversi: una passata
                                    che non ha esaminato nulla e una che ha esaminato record senza
                                    conservarne le righe. Il secondo caso è quello che il messaggio
                                    unico raccontava male — "Nessun record trattato" accanto a "1
                                    record analizzato" nella riga sopra si legge come un errore del
                                    monitoraggio, non come una scelta di configurazione. */}
                                {/* Su una passata ANCORA IN CORSO nessuno dei due messaggi
                                    definitivi è vero: i record possono essere in coda, e le loro
                                    righe le aprirà il consumer una alla volta. Il contatore invece
                                    è già scritto per intero — lo scrive la fase sincrona appena
                                    finisce di valutare — quindi qui il confronto fra i numeri non
                                    dice ancora niente. */}
                                {run.status === 'running'
                                  ? t('automation.monitoring.executionsInProgress')
                                  : !run.logEveryRecord && run.processedItems > 0
                                    ? t('automation.monitoring.executionsNotKept', {
                                        count: run.processedItems,
                                      })
                                    : t('automation.monitoring.noExecutions')}
                              </div>
                            ) : (
                              <table className="simtable">
                                <thead>
                                  <tr>
                                    {recordColumns.map((label) => (
                                      <th key={label}>{label}</th>
                                    ))}
                                    <th>{t('automation.monitoring.colOutcome')}</th>
                                    <th>{t('automation.monitoring.colDuration')}</th>
                                    <th>{t('automation.monitoring.colCause')}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {executions.map((execution) => {
                                    // Calcolate una volta per riga e non dentro il JSX, come nella
                                    // tabella di simulazione: servono due volte — per decidere se
                                    // l'elenco va reso e per renderlo — e su un record che non e'
                                    // stato scartato non si calcolano affatto.
                                    const reasons =
                                      execution.status === 'skipped'
                                        ? exclusionReasons(
                                            execution.failedConditions,
                                            automation?.conditionGroups ?? [],
                                            conditionFields,
                                            t,
                                          )
                                        : [];

                                    const errorOpen =
                                      openExecutionId === execution.automationExecutionId;
                                    // La PRIMA riga di uno stacktrace è "tipo: messaggio", cioè
                                    // l'unica parte che si legge senza indagare; le righe "at ..."
                                    // servono a chi indaga, e stanno nel dettaglio.
                                    const errorSummary =
                                      truncate(
                                        execution.errorMessage?.split('\n')[0]?.trim(),
                                        ERROR_SUMMARY_MAX,
                                      );

                                    return (
                                      <Fragment key={execution.automationExecutionId}>
                                    <tr>
                                      {/* Una cella per COLONNA e non per valore, cercata per
                                          etichetta: le righe che non hanno quella colonna — il
                                          record sparito — restano allineate con un trattino. */}
                                      {recordColumns.map((label) => (
                                        <td key={label}>
                                          {execution.displayValues.find((v) => v.label === label)
                                            ?.value ?? '—'}
                                        </td>
                                      ))}
                                      <td>
                                        <span
                                          className={`status-dot status-dot--${
                                            EXEC_STATUS_DOT[execution.status]
                                          }`}
                                        >
                                          <span className="status-dot__d" />
                                          {t(
                                            `automation.monitoring.${
                                              EXEC_STATUS_LABEL_KEY[execution.status]
                                            }`,
                                          )}
                                        </span>
                                      </td>
                                      <td>
                                        {execution.endDate
                                          ? formatNanos(execution.duration, locale)
                                          : '—'}
                                      </td>
                                      {/* Causa. Su un record SCARTATO non c'e' un errore ma un
                                          verdetto: quali condizioni sono cadute, con la stessa
                                          funzione e la stessa resa della simulazione — la stessa
                                          regola raccontata in due schermate deve leggersi uguale.
                                          Sugli altri stati resta il messaggio, che sui falliti e' lo
                                          stacktrace e sui fermati dalla catena la causa. */}
                                      <td className="text-muted">
                                        {reasons.length === 0 ? (
                                          errorSummary ? (
                                            <button
                                              type="button"
                                              className="mon-error-toggle"
                                              onClick={() =>
                                                setOpenExecutionId(
                                                  errorOpen
                                                    ? null
                                                    : execution.automationExecutionId,
                                                )
                                              }
                                            >
                                              {errorOpen ? '▾' : '▸'} {errorSummary}
                                            </button>
                                          ) : (
                                            '—'
                                          )
                                        ) : (
                                          <ul className="simtable__reasons">
                                            {reasons.map((reason) => (
                                              <li key={reason.group}>
                                                {reasons.length > 1 && (
                                                  <div className="simtable__reasons-group">
                                                    {t('automation.conditions.reasonGroupHeader', {
                                                      index: reason.group,
                                                    })}
                                                  </div>
                                                )}
                                                {reason.conditions.map((condition) => (
                                                  <div
                                                    className="simtable__reasons-item"
                                                    key={condition.automationConditionId}
                                                  >
                                                    {condition.text}
                                                    {condition.value && (
                                                      <>
                                                        {' '}
                                                        <i>{condition.value}</i>
                                                      </>
                                                    )}
                                                  </div>
                                                ))}
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </td>
                                    </tr>

                                        {errorOpen && execution.errorMessage && (
                                          <tr>
                                            <td colSpan={recordColumns.length + 3}>
                                              <div className="mon-error-detail">
                                                <div className="mon-error-detail__title">
                                                  {t('automation.monitoring.errorDetail')}
                                                </div>
                                                <pre className="mon-error-detail__body">
                                                  {execution.errorMessage}
                                                </pre>
                                              </div>
                                            </td>
                                          </tr>
                                        )}
                                      </Fragment>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}

                            {/* Quanti record esaminati NON compaiono nella tabella qui sopra.
                                La condizione è il flag DEL RUN, non la differenza fra i numeri: i
                                numeri dicono "mancano delle righe", che ha due cause diverse — non
                                conservate, oppure non ancora scritte perché la passata è in corso —
                                e sceglierne una a caso farebbe dire alla nota una cosa falsa su un
                                run RUNNING. Il flag del run risponde invece alla domanda giusta,
                                «questa passata conserva tutto?», e al conteggio resta il solo
                                compito di dire quante righe mancano. Non è il flag
                                dell'AUTOMAZIONE, che dice com'è configurata adesso: il run lo ha
                                congelato aprendosi.
                                Sta SOTTO la tabella e non sopra: è una postilla su ciò che si è
                                appena letto, non una premessa a ciò che si sta per leggere. */}
                            {executions && executions.length > 0 && !run.logEveryRecord &&
                              run.status !== 'running' &&
                              run.processedItems > executions.length && (
                                <div className="text-muted p-md">
                                  {t('automation.monitoring.executionsPartiallyKept', {
                                    count: run.processedItems - executions.length,
                                  })}
                                </div>
                              )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </section>
      </div>

      {/* Velo a tutto schermo e non uno spinner dentro il pulsante: la pulizia scorre tutte le
          passate dell'automazione e le cancella una per una, con la cascata di ciascuna, quindi su
          uno storico lungo l'attesa si misura in secondi. Bloccando il puntatore, il velo impedisce
          anche il secondo clic, che manderebbe una seconda pulizia mentre la prima è in volo. */}
      <LoadingOverlay
        open={cleaningEmptyRuns}
        message={t('automation.monitoring.cleaningEmptyRuns')}
      />

      <ConfirmDialog
        open={confirm.isOpen}
        message={confirm.message}
        {...confirm.options}
        onConfirm={confirm.confirm}
        onCancel={confirm.cancel}
      />
    </div>
  );
}

/**
 * Menu azioni di riga del log (icona a tre puntini), reso solo per l'amministratore — come nella
 * colonna azioni dell'elenco, da cui prende anche la resa: stesso bottone `lc-act`, stessa icona.
 * <p>Una voce sola, ed è un menu lo stesso: le azioni di riga di questo prodotto si aprono da lì,
 * e un'icona-cestino nuda accanto a un menu identico due schermate più in là direbbe che le due
 * cose sono diverse. Aggiungerne una seconda domani non cambierà nulla per chi guarda.
 * <p>La conferma la chiede il chiamante, non questo componente: qui si sa quale riga è stata
 * scelta, non che cosa quella scelta comporti.
 */
function RunRowActions({
  automationRunId,
  onDelete,
}: {
  automationRunId: number;
  onDelete: (automationRunId: number) => void;
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const close = () => setAnchorEl(null);

  return (
    <>
      <button
        type="button"
        className="lc-act"
        aria-label={t('automation.monitoring.colActions')}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <MoreVertIcon fontSize="small" />
      </button>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        <MenuItem
          className="text-error"
          onClick={() => {
            close();
            onDelete(automationRunId);
          }}
        >
          {t('automation.monitoring.actionDeleteRun')}
        </MenuItem>
      </Menu>
    </>
  );
}
